<template>
  <Dialog
    v-model:visible="visibleValue"
    modal
    :draggable="false"
    dismissable-mask
    :header="$t('components.structural.workspace.creation-dialog.title')"
    :style="{ maxWidth: '735px' }"
  >
    <FormsForm
      :schema="schema"
      :initial-values="initialValues"
      :on-submit="createWorkspace"
    >
      <FormsFieldsInputText
        name="displayName"
        :label="
          $t(
            'components.structural.workspace.creation-dialog.form.display-name.label',
          )
        "
        :placeholder="
          $t(
            'components.structural.workspace.creation-dialog.form.display-name.placeholder',
          )
        "
      >
        <template #left>
          <IconPicker name="icon" />
        </template>
      </FormsFieldsInputText>

      <template v-if="!forcedWorkspaceVisibility">
        <FormsFieldsRadioButton
          name="visibility"
          :value="WorkspaceVisibility.Public"
          input-id="visibility-public"
          :label="
            $t(
              'components.structural.workspace.creation-dialog.form.visibility.public.label',
            )
          "
          :sub-label="
            $t(
              'components.structural.workspace.creation-dialog.form.visibility.public.sub-label',
            )
          "
          class="mb-4"
        />
        <FormsFieldsRadioButton
          name="visibility"
          :value="WorkspaceVisibility.Private"
          input-id="visibility-private"
          :label="
            $t(
              'components.structural.workspace.creation-dialog.form.visibility.private.label',
            )
          "
          :sub-label="
            $t(
              'components.structural.workspace.creation-dialog.form.visibility.private.sub-label',
            )
          "
        />
      </template>

      <template v-if="hasAdditionalFormFields">
        <Divider />
        <component :is="additionalFormFields" />
      </template>

      <template #footer>
        <Button
          :label="$t('common.cta.cancel')"
          severity="secondary"
          @click="$emit('hide')"
        />
      </template>
    </FormsForm>
  </Dialog>
</template>

<script lang="ts" setup>
import { object, string } from 'yup'
import { storeToRefs } from 'pinia'
import IconPicker from '~/components/forms/fields/IconPicker.vue'
import { useOrganizationsStore } from '~/store/organizations'
import type { Workspace, WorkspaceToCreate } from '~/types/workspaces'
import { AdditionalFormsFields } from '~/constants/components/additional'
import { CloudFunctions } from '~/constants/firebase/cloud-functions'
import { WorkspaceVisibility } from '~/constants/workspaces'
import { getAdditionalFormFields } from '~/utils/components'

const props = withDefaults(
  defineProps<{
    visible: boolean
    referenceCreator?: boolean
    forcedWorkspaceVisibility?: WorkspaceVisibility
  }>(),
  {
    referenceCreator: true,
  },
)
const emits = defineEmits(['hide', 'created'])

const visibleValue = computed({
  get: () => props.visible,
  set: () => emits('hide'),
})

const organizationStore = useOrganizationsStore()
const { selectedOrganization } = storeToRefs(organizationStore)

const { hasAdditionalFormFields, additionalFormFields, schema }
  = getAdditionalFormFields(
    AdditionalFormsFields.Workspace.CreationDialog,
    object().shape({
      icon: object().shape({
        type: string().required(),
        value: string().required(),
      }).required(),
      displayName: string().min(3).required(),
      visibility: string().required(),
    }),
  )
const initialValues = {
  displayName: '',
  visibility: props.forcedWorkspaceVisibility ?? WorkspaceVisibility.Public,
}

// TODO: Type
const createWorkspace = async ({ displayName, visibility, ...rest }: any) => {
  const { data: workspace } = await useHttpsCallable<
    WorkspaceToCreate,
    Workspace
  >(CloudFunctions.CreateWorkspace, {
    displayName,
    visibility,
    organizationId: selectedOrganization.value!.id,
    referenceCreator: props.referenceCreator,
    ...rest,
  })
  emits('created', workspace)
}
</script>
